import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Mail, ShieldCheck } from "lucide-react";
import { settingsApi, type GeneralSettings, type SSOSettings } from "@/services/settings";
import { rbacApi } from "@/services/rbac";
import { uploadReceipt } from "@/services/uploads";
import { apiConfig } from "@/services/api";
import { useCan } from "@/auth/permissions";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { SettingsTabs } from "./SettingsTabs";
import { ChannelSeatsSwitchCard } from "./ChannelSeatsSwitchCard";   // CHANAV-3
import { BRAND_PLACEHOLDER, MISSING_NOTE, SAMPLE_PHONE_NOTE, isSamplePhone } from "./brandFields";

const apiImg = (u: string) => (u.startsWith("http") ? u : `${apiConfig.base}${u}`);
const ROLE_CN: Record<string, string> = {
  owner: "老板", tech_admin: "超级管理员", finance: "财务",
  op_manager: "OP 经理", op_supervisor: "OP 主管", op: "OP", account_manager: "客户经理", sales: "业务员",
};

export function GeneralSettingsPage() {
  const qc = useQueryClient();
  const canEdit = useCan("system.settings.update");

  const gen = useQuery({ queryKey: ["settings", "general"], queryFn: settingsApi.getGeneral });
  const sso = useQuery({ queryKey: ["settings", "sso"], queryFn: settingsApi.getSso });
  const users = useQuery({ queryKey: ["rbac", "users"], queryFn: rbacApi.listUsers });

  const [g, setG] = useState<GeneralSettings | null>(null);
  const [ssoForm, setSso] = useState<SSOSettings | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  useEffect(() => { if (gen.data && !g) setG(gen.data); }, [gen.data, g]);
  useEffect(() => { if (sso.data && !ssoForm) setSso(sso.data); }, [sso.data, ssoForm]);

  const saveGeneral = useMutation({
    mutationFn: (p: GeneralSettings) => settingsApi.patchGeneral(p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings", "general"] }); qc.invalidateQueries({ queryKey: ["branding"] }); },
    onError: () => alert("保存失败，请重试"),
  });
  const saveSso = useMutation({
    mutationFn: (p: SSOSettings) => settingsApi.patchSso(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "sso"] }),
    onError: () => alert("SSO 配置保存失败，请重试"),
  });
  async function onPickLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f || !g) return;
    setUploadingLogo(true);
    try { const r = await uploadReceipt(f, f.name || "logo.png", "public"); setG({ ...g, logo_url: r.url }); } // 品牌 Logo·全站/邮件公开露出，SEC-2 显式声明公开
    finally { setUploadingLogo(false); if (e.target) e.target.value = ""; }
  }

  if (gen.isError || sso.isError)
    return <ErrorState message="系统设置加载失败" onRetry={() => { gen.refetch(); sso.refetch(); }} />;
  if (!g || !ssoForm) return <LoadingState />;
  const list = users.data ?? [];
  const total = list.length;
  const ssoEnabled = ssoForm.google_enabled || ssoForm.microsoft_enabled;

  return (
    <PageShell title="系统设置 · 通用设置" subtitle="公司信息 + 员工身份认证 SSO + 业务参数 · 仅老板 + 技术管理员可改">
      <SettingsTabs />
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
        <Lock size={14} /> 权限：老板 + 技术管理员可编辑 · 其它角色只读 · 关键改动写审计日志
      </div>

      {/* 🏢 公司信息 */}
      <Card title="🏢 公司信息" hint="印发票 / 合同 / 邮件签名用 · 不要轻易改" onSave={canEdit ? () => saveGeneral.mutate(g) : undefined} saving={saveGeneral.isPending}>
        <Field label="公司名称（中文）*" missing={!g.name_cn.trim()}><Input value={g.name_cn} onChange={(v) => setG({ ...g, name_cn: v, legal_name: v })} placeholder={BRAND_PLACEHOLDER.name_cn} /></Field>
        <Field label="公司名称（英文）*"><Input value={g.name_en} onChange={(v) => setG({ ...g, name_en: v })} placeholder={BRAND_PLACEHOLDER.name_en} /></Field>
        <Field label="简称（侧边栏 / 同行站 / 司导端抬头显示）" missing={!g.brand_name.trim()}><Input value={g.brand_name} onChange={(v) => setG({ ...g, brand_name: v })} placeholder={BRAND_PLACEHOLDER.brand_name} /></Field>
        <Field label="Logo">
          <div className="flex items-center gap-3">
            {g.logo_url ? <img src={apiImg(g.logo_url)} alt="logo" className="h-10 w-10 rounded-lg border border-slate-200 object-cover" />
              : <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">⛰</div>}
            <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50">
              {uploadingLogo ? "上传中…" : g.logo_url ? "替换" : "上传"}
              <input type="file" accept="image/*" className="hidden" onChange={onPickLogo} disabled={uploadingLogo || !canEdit} />
            </label>
            {g.logo_url && <button type="button" onClick={() => setG({ ...g, logo_url: null })} className="text-[12px] text-rose-600 hover:underline">移除</button>}
          </div>
        </Field>
        <Field label="税号 / EIN *"><Input value={g.tax_id ?? ""} onChange={(v) => setG({ ...g, tax_id: v })} placeholder="US-EIN-XX-XXXXXXX" /></Field>
        <Field label="注册地 *"><Input value={g.registered_address} onChange={(v) => setG({ ...g, registered_address: v })} placeholder={BRAND_PLACEHOLDER.registered_address} /></Field>
        <Field label="联系电话" missing={!g.contact_phone.trim()} warn={isSamplePhone(g.contact_phone) ? SAMPLE_PHONE_NOTE : undefined}><Input value={g.contact_phone} onChange={(v) => setG({ ...g, contact_phone: v })} placeholder={BRAND_PLACEHOLDER.contact_phone} type="tel" /></Field>
        <Field label="客服邮箱" missing={!g.contact_email.trim()}><Input value={g.contact_email} onChange={(v) => setG({ ...g, contact_email: v })} type="email" placeholder={BRAND_PLACEHOLDER.contact_email} /></Field>
        <Field label="网站"><Input value={g.website ?? ""} onChange={(v) => setG({ ...g, website: v })} placeholder="例：https://www.公司域名.com" /></Field>
        <Field label="旅行社执照号（手机站信任条和页脚显示；空着则信任条不显示）"><Input value={g.business_license ?? ""} onChange={(v) => setG({ ...g, business_license: v })} /></Field>
        <Field label="公司 CUA 号"><Input value={g.cua_number ?? ""} onChange={(v) => setG({ ...g, cua_number: v })} placeholder="进凭证抬头·留空则凭证不显示该行" /></Field>
      </Card>

      {/* 🔐 员工身份认证 SSO */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-base font-semibold">🔐 员工身份认证（SSO）</h3>
          {canEdit && <button disabled={saveSso.isPending} onClick={() => saveSso.mutate(ssoForm)} className="h-9 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50">{saveSso.isPending ? "保存中…" : "保存 SSO"}</button>}
        </div>
        <p className="mb-4 text-[12px] text-slate-500">全局 SSO 策略在这配 · 单用户绑定关系在「权限管理 - 用户管理」</p>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Mail size={18} className="text-slate-500" /><div><div className="text-[13px] font-semibold">邮箱 + 密码（默认）</div><div className="text-[11px] text-slate-500">当前 {total} 员工使用</div></div></div>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">启用</span>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">密码强度 ≥ 8 位含大小写数字 · 错误锁定 5 次 30min · 强制改密 90 天</div>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><span className="text-lg">🔵</span><div><div className="text-[13px] font-semibold">Google Workspace SSO</div><div className="text-[11px] text-slate-500">{ssoForm.google_enabled ? "已启用" : "未启用"}</div></div></div>
              <Toggle on={ssoForm.google_enabled} onClick={() => canEdit && setSso({ ...ssoForm, google_enabled: !ssoForm.google_enabled })} />
            </div>
            {ssoForm.google_enabled && (
              <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                <Field label="Workspace 域名"><Input value={ssoForm.google_hd_domain ?? ""} onChange={(v) => setSso({ ...ssoForm, google_hd_domain: v })} placeholder="例：公司域名.com" /></Field>
                <Field label="OAuth Client ID"><Input value={ssoForm.google_client_id ?? ""} onChange={(v) => setSso({ ...ssoForm, google_client_id: v })} /></Field>
              </div>
            )}
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><span className="text-lg">🟦</span><div><div className="text-[13px] font-semibold">Microsoft 365 SSO</div><div className="text-[11px] text-slate-500">适用 B2B 客户用 Microsoft 账号登录（v2）</div></div></div>
              <Toggle on={ssoForm.microsoft_enabled} onClick={() => canEdit && setSso({ ...ssoForm, microsoft_enabled: !ssoForm.microsoft_enabled })} />
            </div>
          </div>
        </div>

        {/* 全局策略 + 统计 */}
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-3 text-[12px] text-slate-600">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-slate-700"><ShieldCheck size={14} /> 全局策略</div>
            ☑ MFA 双因素（强制）：老板 + 财务 + 技术管理员<br />☑ MFA 双因素（可选）：其它角色<br />Session 超时：8h · 闲置自动登出<br />IP 白名单：办公室 + 远程 VPN
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="总员工" value={String(total)} />
            <Stat label="SSO 通道" value={ssoEnabled ? "已启用" : "未启用"} />
            <Stat label="当前登录方式" value={ssoEnabled ? "SSO + 邮箱密码" : "邮箱密码"} />
            <Stat label="单用户 SSO 绑定" value="待接 v2" />
          </div>
        </div>

        {/* 员工 SSO 状态 */}
        <div className="mt-4">
          <div className="mb-2 text-[13px] font-semibold text-slate-700">👥 员工 SSO 状态（{total} 人）</div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-slate-500"><tr><th className="px-3 py-2 text-left">员工</th><th className="px-3 py-2 text-left">角色</th><th className="px-3 py-2 text-left">默认登录方式</th><th className="px-3 py-2 text-left">上次登录</th></tr></thead>
              <tbody>
                {list.slice(0, 8).map((u) => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{u.name}</td>
                    <td className="px-3 py-2 text-slate-500">{ROLE_CN[u.primary_role] ?? u.primary_role}</td>
                    <td className="px-3 py-2"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">{ssoForm.google_enabled ? "Google Workspace" : "邮箱密码"}</span></td>
                    <td className="px-3 py-2 text-slate-400">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("zh-CN") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 💼 业务参数 */}
      <Card title="💼 业务参数" hint="系统默认值 · 影响订单 / 财务 / 报表" onSave={canEdit ? () => saveGeneral.mutate(g) : undefined} saving={saveGeneral.isPending}>
        <Field label="默认货币 *"><Input value={g.currency} onChange={(v) => setG({ ...g, currency: v })} /></Field>
        <Field label="备用币种"><Input value={g.secondary_currency} onChange={(v) => setG({ ...g, secondary_currency: v })} /></Field>
        <Field label="汇率源"><Input value={g.fx_source} onChange={(v) => setG({ ...g, fx_source: v })} /></Field>
        <Field label="财务月结日"><Input type="number" value={String(g.fiscal_close_day)} onChange={(v) => setG({ ...g, fiscal_close_day: Number(v) })} /></Field>
        <Field label="默认时区"><Input value={g.timezone} onChange={(v) => setG({ ...g, timezone: v })} /></Field>
        <Field label="工作时间（影响通知静默）"><Input value={g.working_hours} onChange={(v) => setG({ ...g, working_hours: v })} /></Field>
        <Field label="同行站下单等待确认时长（分钟）">
          <Input type="number" value={String(g.partner_order_hold_minutes ?? 120)} onChange={(v) => setG({ ...g, partner_order_hold_minutes: Number(v) })} placeholder="120" />
          <span data-testid="hold-minutes-hint" className="text-[11px] text-slate-400">同行在同行站提交订单后，座位先占住这么久等我们确认；到点没确认就自动取消、释放座位并通知同行。默认 120 分钟（10 ~ 2880），只影响之后新提交的单。</span>
        </Field>
        <Field label="人工确认订单占位时长（分钟）">
          <Input type="number" value={String(g.manual_order_hold_minutes ?? 1440)} onChange={(v) => setG({ ...g, manual_order_hold_minutes: Number(v) })} placeholder="1440" />
          <span data-testid="manual-hold-hint" className="text-[11px] text-slate-400">产品「确认方式」是人工确认（或还没设置）时，同行站 / 开放平台 / 携程 / Klook 的订单先「待确认」，座位占这么久等 OP 确认或拒绝；到点没处理自动取消。默认 1440 分钟（24 小时，10 ~ 4320）。即时确认的产品检查没过转人工的，仍用上面的 120 分钟（携程 24 小时不变）</span>
        </Field>
        <Field label="订单号前缀"><Input value={g.order_no_prefix} onChange={(v) => setG({ ...g, order_no_prefix: v })} /></Field>
        <Field label="Invoice 号前缀（QB 同步）"><Input value={g.invoice_no_prefix} onChange={(v) => setG({ ...g, invoice_no_prefix: v })} /></Field>
      </Card>

      {/* 💳 同行结算（CREDIT-1）· 全局默认；单个同行可在「同行详情 · 信用 & 结算」里单独覆盖 */}
      <Card title="💳 同行结算" hint="月结 / 单结同行的应收到期规则与逾期停单 · 全局默认，单个同行可在同行详情里单独设" onSave={canEdit ? () => saveGeneral.mutate(g) : undefined} saving={saveGeneral.isPending}>
        <Field label="月结到期日（出发月的次月几号）">
          <Input type="number" value={String(g.partner_monthly_due_day ?? 15)} onChange={(v) => setG({ ...g, partner_monthly_due_day: Number(v) })} placeholder="15" />
          <span className="text-[11px] text-slate-400">默认 15（1 ~ 28）：例如 10 月出发的订单，11 月 15 号到期。只影响之后新生成的应收。</span>
        </Field>
        <Field label="单结到期天数（出发后几天）">
          <Input type="number" value={String(g.partner_per_order_due_days ?? 7)} onChange={(v) => setG({ ...g, partner_per_order_due_days: Number(v) })} placeholder="7" />
          <span className="text-[11px] text-slate-400">默认 7（0 ~ 90）：每单出发日之后第 N 天到期。</span>
        </Field>
        <Field label="逾期自动停新单（超过几天）">
          <Input type="number" value={String(g.partner_block_after_overdue_days ?? 15)} onChange={(v) => setG({ ...g, partner_block_after_overdue_days: Number(v) })} placeholder="15" />
          <span className="text-[11px] text-slate-400">默认 15（0 ~ 365）：有应收逾期超过这么多天，该同行暂停接收新订单；付清自动恢复，老板也可手动解锁 7 天。</span>
        </Field>
      </Card>

      {/* 📡 渠道余位（CHANAV-3）· 开关独立保存（不跟上面的整包保存走），切换要二次确认 */}
      <ChannelSeatsSwitchCard />
    </PageShell>
  );
}

function Card({ title, hint, onSave, saving, children }: { title: string; hint?: string; onSave?: () => void; saving?: boolean; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-4 flex items-start justify-between">
        <div><h3 className="text-base font-semibold">{title}</h3>{hint && <p className="mt-0.5 text-[12px] text-slate-500">{hint}</p>}</div>
        {onSave && <button type="button" disabled={saving} onClick={onSave} className="h-9 shrink-0 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50">{saving ? "保存中…" : "保存"}</button>}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </section>
  );
}
/** missing = 对外字段没填（红字说明哪里不显示）；warn = 已填但可疑（黄字，只提示不改数据）。 */
function Field({ label, children, missing, warn }: { label: string; children: React.ReactNode; missing?: boolean; warn?: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] font-medium text-slate-700">{label}</span>
      {children}
      {missing && <span data-testid="field-missing" className="text-[11px] text-rose-600">{MISSING_NOTE}</span>}
      {warn && <span data-testid="field-sample-warn" className="text-[11px] font-medium text-amber-600">⚠ {warn}</span>}
    </label>
  );
}
function Input({ value, onChange, type = "text", placeholder }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
    className="h-9 rounded-lg border border-slate-200 px-3 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />;
}
function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <button type="button" onClick={onClick} className={`h-6 w-11 rounded-full p-0.5 transition ${on ? "bg-blue-600" : "bg-slate-300"}`}><span className={`block h-5 w-5 rounded-full bg-white transition ${on ? "translate-x-5" : ""}`} /></button>;
}
function Stat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-[11px] text-slate-500">{label}</div><div className="text-[14px] font-bold text-slate-900">{value}</div></div>;
}
